import classNames from 'classnames';
import React, { FunctionComponent, useState } from 'react';
import { ReactComponent as CloseIcon } from 'assets/icons/Close.svg';
import { useId } from 'react-id-generator';
import { ReactComponent as DropdownIcon } from 'assets/icons/Dropdown.svg';
import { useCities } from 'providers/cities';
import CitiesModal from './cities-modal';
import Button from 'components/button';
import { useNavigate } from 'hooks/useNavigate';
import { ROUTES } from 'constants/routes';
import UserButton from './user-button';
import { ReactComponent as ChevronRightIcon } from 'assets/icons/Chevron right.svg';
import { useAuth } from 'providers/auth';
import { useFooterLinks } from 'hooks/useFooterLinks';
import { useProtect } from 'hooks/useProtect';
import { generatePath } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../language-switcher';
import { Box, Tooltip } from '@mui/material';

interface MenuProps {
  open: boolean;
  onClose: () => void;
  links: { handle: () => void; label: string; hide?: boolean }[];
}

const Menu: FunctionComponent<MenuProps> = ({ open, onClose, links }) => {
  const ids = useId(links.length);
  const [openCityModal, setOpenCityModal] = useState(false);
  const { selectedCity } = useCities();
  const navigate = useNavigate();
  const { user } = useAuth();
  const footerLinks = useFooterLinks();
  const protect = useProtect();
  const { t } = useTranslation();

  const newReportButton = () => {
    if (selectedCity?.problem_submission_message) {
      return (
        <Tooltip title={selectedCity?.problem_submission_message}>
          <Box>
            <Button
              className='w-full pointer-events-none disabled:opacity-50'
              disabled={true}
            >
              {t('header.newReport')}
            </Button>
          </Box>
        </Tooltip>
      );
    }

    return (
      <Button
        className='w-full'
        onClick={() =>
          protect(() => navigate(generatePath(ROUTES.newMessage, { '*': '0' })))
        }
      >
        {t('header.newReport')}
      </Button>
    );
  };

  return (
    <>
      <CitiesModal open={openCityModal} close={() => setOpenCityModal(false)} />
      <div
        className={classNames(
          'fixed z-[1000] right-0 top-0 px-6 py-7 overflow-auto select-none bg-theme-white w-[290px] flex flex-col h-full transform transition duration-300',
          {
            'pointer-events-none translate-x-full': !open,
            'translate-x-0 shadow-[-10px_0px_32px_rgba(0,0,0,0.15)]': open,
          },
        )}
      >
        <div className='flex justify-between items-center border-b border-transparent'>
          <div
            onClick={() => setOpenCityModal((prev) => !prev)}
            className='flex items-center cursor-pointer'
          >
            <div className='capitalize mr-3'>
              {selectedCity?.cityName.split(',')[0]}
            </div>
            <DropdownIcon className='w-4 h-4' />
          </div>
          <div className='flex mr-4'>
            <LanguageSwitcher />
          </div>
          <CloseIcon className='cursor-pointer' onClick={onClose} />
        </div>
        <div className='flex-grow flex items-center my-7'>
          <div className='w-full'>
            <div className='mb-6'>
              {links.map((link, i) =>
                link.hide ? null : (
                  <div
                    onClick={link.handle}
                    className='h-20 cursor-pointer flex items-center justify-center text-lg font-bold'
                    key={ids[i]}
                  >
                    {link.label}
                  </div>
                ),
              )}
            </div>
            {!user ? null : (
              <div
                onClick={() => navigate(ROUTES.profile)}
                className='flex justify-between cursor-pointer items-center h-[52px] mb-7'
              >
                <UserButton />
                <ChevronRightIcon />
              </div>
            )}
            {newReportButton()}
          </div>
        </div>
        <div>
          {footerLinks.map(({ icon: Icon, title, href, id, onClickEvent }) => (
            <a
              {...(onClickEvent
                ? { onClick: onClickEvent, href: href }
                : { onClick: () => (window.location.href = href), href: href })}
              key={id}
              className='flex items-center py-3'
            >
              <Icon className='mr-3 w-6' />
              <div>{title}</div>
            </a>
          ))}
        </div>
      </div>
    </>
  );
};

export default Menu;
